<template>
    <div class="producttype-sawline-edit-page">
        <pre
            class="debug"
            v-if="isDebug"
            >{{ productlistDefinitionOptions }}</pre
        >
        <div class="actions">
            <ui5-button @click="back()">
                {{ trans('pages.product_type_sawline_edit.cancel') }}
            </ui5-button>
            <ui5-button
                @click="save()"
                v-if="!loading"
            >
                {{ trans('pages.product_type_sawline_edit.save') }}
            </ui5-button>
            <ui5-button
                id="to-debug-sawlines"
                @click="toDebugSawlines()"
            >
                {{ trans('pages.product_type_sawline_edit.to_debug_sawlines') }}
            </ui5-button>
        </div>
        <spinner-text
            class="ajax"
            v-if="loading"
        >
            &nbsp;{{ trans('pages.product_type_sawline_edit.loading') }}
        </spinner-text>
        <spinner-text
            class="ajax"
            v-else-if="saving"
        >
            &nbsp;{{ trans('pages.product_type_sawline_edit.saving') }}
        </spinner-text>
        <template v-else-if="productType">
            <h1>{{ productType.label }} (#{{ productType.producttypeId }})</h1>
            <ul
                class="condition-errors"
                v-if="conditionErrors.length > 0"
            >
                <li
                    v-for="(error, index) in conditionErrors"
                    :key="'conditionError.' + index"
                    v-html="
                        trans(
                            'pages.product_type_sawline_edit.condition_link_error',
                            {
                                externalName: error.externalName,
                                sawlineId: error.sawlineId,
                                conditionId: error.conditionId,
                            }
                        )
                    "
                ></li>
            </ul>
            <div class="conditions">
                <div class="section-title">
                    <h2 id="conditions-title">
                        {{
                            trans(
                                'pages.product_type_sawline_edit.conditions_title'
                            )
                        }}
                    </h2>
                    <ui5-button @click="addCondition()">+</ui5-button>
                </div>
                <div v-for="(v, conditionIndex) in $v.conditions.$each.$iter">
                    <div
                        v-if="v.removed.$model"
                        class="removed"
                    >
                        {{
                            trans(
                                'pages.product_type_sawline_edit.removed_condition'
                            )
                        }}
                        (
                        <a @click="v.removed.$model = false">
                            {{
                                trans('pages.product_type_sawline_edit.restore')
                            }}
                        </a>
                        )
                    </div>
                    <template v-else>
                        <i
                            class="fa fa-trash-o"
                            @click="v.removed.$model = true"
                        ></i>
                        <ui5-select
                            @change="
                                v.datafield.$model =
                                    $event.detail.selectedOption.value
                            "
                        >
                            <ui5-option
                                :class="{ 'inactive-option': !option.active }"
                                v-for="(
                                    option, optionIndex
                                ) in dataFieldOptions"
                                :key="optionIndex"
                                :selected="option.value == v.datafield.$model"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </ui5-option>
                        </ui5-select>
                        <span
                            v-if="getErrorFromValidator(v.datafield).length"
                            class="error"
                        >
                            {{ getErrorFromValidator(v.datafield) }}
                        </span>
                        <ui5-select
                            @change="
                                v.compare.$model =
                                    $event.detail.selectedOption.value
                            "
                        >
                            <ui5-option
                                v-for="(
                                    option, optionIndex
                                ) in compareOperatorOptions"
                                :key="optionIndex"
                                :selected="option.value == v.compare.$model"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </ui5-option>
                        </ui5-select>
                        <span
                            v-if="getErrorFromValidator(v.compare).length"
                            class="error"
                        >
                            {{ getErrorFromValidator(v.compare) }}
                        </span>
                        <ui5-input
                            type="Text"
                            :value="v.cmpValue.$model"
                            @input="v.cmpValue.$model = $event.target.value"
                        />
                        <span
                            v-if="getErrorFromValidator(v.cmpValue).length"
                            class="error"
                        >
                            {{ getErrorFromValidator(v.cmpValue) }}
                        </span>
                        <span
                            v-if="getErrorFromValidator(v, true).length"
                            class="error"
                        >
                            {{ getErrorFromValidator(v, true) }}
                        </span>
                        <span
                            class="inactive-option"
                            v-if="
                                dataFields[v.datafield.$model] &&
                                dataFields[v.datafield.$model].inUse == 0
                            "
                            @mouseover="
                                $refs.toolTipCondition[conditionIndex].showAt(
                                    $event.target
                                )
                            "
                            @mouseout="
                                $refs.toolTipCondition[conditionIndex].close()
                            "
                        >
                            !
                        </span>
                        <ui5-popover
                            ref="toolTipCondition"
                            :allow-target-overlap="false"
                            placement-type="Bottom"
                        >
                            <div>
                                {{
                                    trans(
                                        'pages.product_type_sawline_edit.inactive_datafield_warning'
                                    )
                                }}
                            </div>
                        </ui5-popover>

                        <span
                            v-for="error in v.backendErrors.$model"
                            class="error backend-error"
                        >
                            {{ error }}
                        </span>
                    </template>
                </div>
                <div class="section-title">
                    <h2>
                        {{
                            trans(
                                'pages.product_type_sawline_edit.sawlines_title'
                            )
                        }}
                    </h2>
                    <ui5-button @click="addSawline()">+</ui5-button>
                    <div class="condition-filter-container">
                        <ui5-label for="conditionFilter">
                            {{
                                trans(
                                    'pages.product_type_sawline_edit.filter_conditions_label'
                                )
                            }}
                        </ui5-label>
                        <ui5-multi-combobox
                            @selection-change="conditionFilterChange"
                        >
                            <ui5-mcb-item
                                :selected="conditionFilters.includes(condition)"
                                :text="
                                    getDatafieldLabel(
                                        dataFields[condition.datafield]
                                    ) +
                                    ' ' +
                                    condition.compare +
                                    ' ' +
                                    condition.cmpValue
                                "
                                :key="conditionIndex"
                                v-for="(
                                    condition, conditionIndex
                                ) in conditions.filter((x) => !x.removed)"
                            ></ui5-mcb-item>
                        </ui5-multi-combobox>
                    </div>
                </div>
                <div
                    class="sawlines"
                    v-if="sawlines && conditions && dataFields"
                >
                    <sawline-edit
                        :id="'sawline-' + sawlineIndex"
                        ref="sawline-edit"
                        v-for="(sawline, sawlineIndex) in sawlines['']"
                        :key="'sawline-' + sawlineIndex"
                        :conditions="conditions"
                        :dataFields="dataFields"
                        :productlist-definition-options="
                            productlistDefinitionOptions
                        "
                        :dataFieldOptions="dataFieldOptions"
                        :profileOptions="profileOptions"
                        :profileDatafieldOptions="profileDatafieldOptions"
                        :get-datafield-label="getDatafieldLabel"
                        :saw-group-names="sawGroupNames"
                        :open-line="openLine"
                        :selected-line="selectedLine"
                        @copy="copySawline"
                        :condition-filters="conditionFilters"
                        :children="sawlines[sawline.ID]"
                        :add-child-sawline="addChildSawline"
                        :sawline="sawline"
                    />
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import { maxLength, requiredIf } from 'vuelidate/lib/validators';
import SpinnerText from '../components/loader/SpinnerText';
import productApi from '../api/product';
import { getDatafieldLabel } from '../helpers/sawline-common';
import { showErrorMessage } from '../dialogs';
import IsDebugMixin from '../mixins/is-debug';
import _ from 'lodash';
import { mapActions } from 'vuex';
import localization from '../mixins/localization';

export default {
    mixins: [IsDebugMixin, localization],
    mounted() {
        window.getAllComponents = this.getAllComponents;
        this.loadOptions();
        this.id = this.$route.params.id;
        this.loading = true;
        productApi
            .rulesEdit(this.id)
            .then((response) => {
                this.setData(response.data);
            })
            .finally(() => {
                this.loading = false;
            });
    },
    data() {
        return {
            id: null,
            loading: false,
            saving: false,
            profileOptions: null,
            profileDatafieldOptions: null,
            productType: null,
            conditions: null,
            dataFields: null,
            sawGroupNames: null,
            sawlines: null,
            compareOperators: ['=', '!=', '>', '>=', '<', '<='],
            conditionFilters: [],
            selectedLine: null,
            conditionErrors: [],
            productlistDefinitionOptions: null,
        };
    },
    methods: {
        ...mapActions({
            loadOptions: 'sawlines/fetchEditOptionsAsNeeded',
        }),
        openLine(line) {
            this.selectedLine = line;
        },
        conditionFilterChange(event) {
            this.conditionFilters = event.detail.items.map((selectedItem) => {
                const parent = selectedItem.parentElement;
                const index = Array.prototype.indexOf.call(
                    parent.children,
                    selectedItem
                );
                const condition = this.conditions[index];
                return condition;
            });
        },
        getDatafieldLabel,
        toDebugSawlines() {
            this.$router.push('/debugsawlines');
        },
        setData(data) {
            const conditions = data.data.conditions;
            this.processawConditions(conditions);
            this.conditions = conditions;
            this.productType = data.data.type;
            this.dataFields = data.data.datafields;
            this.sawGroupNames = data.data.sawGroupNames;
            const profileOptions = data.data.profileOptions;
            profileOptions.unshift({
                value: null,
                label: this.trans('pages.product_type_sawline_edit.no_profile'),
            });
            this.profileOptions = profileOptions;
            const profileDatafieldOptions = data.data.profileDatafieldOptions;
            profileDatafieldOptions.unshift({
                value: null,
                label: this.trans(
                    'pages.product_type_sawline_edit.no_profile_datafield'
                ),
            });
            this.profileDatafieldOptions = profileDatafieldOptions;
            this.productlistDefinitionOptions =
                data.data.productlistDefinitions.map((x) => ({
                    value: x.ID,
                    label: x.label,
                }));
            const lines = {};
            this.conditionErrors = [];
            for (const key in data.data.sawlines) {
                const sawlines = data.data.sawlines[key];
                sawlines.forEach((sawline) => {
                    sawline.conditions = [];
                    sawline.selectedConditionToAdd = null;
                    sawline.removed = false;
                    sawline.backendErrors = [];
                    sawline.sawConditions.forEach((conditionId) => {
                        const condition = this.conditions.find(
                            (x) => x.ID == conditionId
                        );
                        if (!condition) {
                            this.conditionErrors.push({
                                conditionId,
                                sawlineId: sawline.ID,
                                externalName: sawline.externalName,
                            });
                        } else {
                            sawline.conditions.push(condition);
                        }
                    });
                    const arr = lines[key] || [];
                    arr.push(sawline);
                    lines[key] = arr;
                });
            }

            this.sawlines = lines;
        },
        processawConditions(conditions) {
            conditions.forEach((x) => {
                x.removed = false;
                x.backendErrors = [];
            });
            conditions.sort((a, b) => {
                if (a == b) {
                    return 0;
                }
                return a.ID > b.ID ? 1 : -1;
            });
        },
        getAllComponents() {
            return getComponents(this);

            function getComponents(component) {
                const components = [];
                if (component.$refs) {
                    Object.values(component.$refs)
                        .flat()
                        .forEach((x) => {
                            components.push(x);
                            getComponents(x).forEach((y) => {
                                components.push(y);
                            });
                        });
                }
                return components;
            }
        },
        save() {
            let valid = true;
            this.getAllComponents().forEach((x) => {
                if (x.$v) {
                    x.$v.$touch();
                    if (x.$v.$invalid) {
                        valid = false;
                    }
                }
            });

            if (!valid) {
                showErrorMessage(
                    this.trans(
                        'pages.product_type_sawline_edit.resolve_input_errors'
                    )
                );
            } else {
                this.saving = true;

                this.flatSawlines.forEach((x) => {
                    x.backendErrors = [];
                });
                this.conditions.forEach((x) => {
                    x.backendErrors = [];
                });

                productApi
                    .saveRules(
                        this.id,
                        JSON.parse(JSON.stringify(this.conditions)),
                        (this.sawlines[''] || []).map((x) =>
                            this.getSawlinePayload(x)
                        )
                    )
                    .then((response) => {
                        this.setData(response.data);
                        this.saving = false;
                    })
                    .catch((errorData) => {
                        const error = errorData.response.data;

                        let conditionId = null;
                        if (error.condition) {
                            const currentLang = this.currentLanguage;
                            conditionId =
                                this.getDatafieldLabel(
                                    this.dataFields[error.condition.datafield],
                                    currentLang
                                ) +
                                ' ' +
                                error.condition.compare +
                                ' ' +
                                error.condition.cmpValue;
                        }

                        let errorMsg = '';
                        switch (error.error) {
                            case 'sawline.empty':
                                errorMsg = this.trans(
                                    'pages.product_type_sawline_edit.field_empty_error',
                                    {
                                        field: error.field,
                                        index: error.index + 1,
                                    }
                                );
                                this.flatSawlines[error.index].backendErrors = [
                                    errorMsg,
                                ];
                                showErrorMessage(errorMsg, () => {
                                    document
                                        .querySelector(
                                            '#sawline-' + error.index
                                        )
                                        .scrollIntoView();
                                });
                                break;
                            case 'sawline.invalid':
                                errorMsg = this.trans(
                                    'pages.product_type_sawline_edit.field_invalid_error',
                                    {
                                        field: error.field,
                                        index: error.index + 1,
                                    }
                                );
                                showErrorMessage(errorMsg, () => {
                                    document
                                        .querySelector(
                                            '#sawline-' + error.index
                                        )
                                        .scrollIntoView();
                                });
                                this.flatSawlines[error.index].backendErrors = [
                                    errorMsg,
                                ];
                                break;
                            case 'sawline.range':
                                errorMsg = this.trans(
                                    'pages.product_type_sawline_edit.field_out_of_range_error',
                                    {
                                        field: error.field,
                                        index: error.index + 1,
                                        min: error.min,
                                        max: error.max,
                                    }
                                );
                                showErrorMessage(errorMsg, () => {
                                    document
                                        .querySelector(
                                            '#sawline-' + error.index
                                        )
                                        .scrollIntoView();
                                });
                                this.flatSawlines[error.index].backendErrors = [
                                    errorMsg,
                                ];
                                break;
                            case 'sawcondition.empty':
                                errorMsg = this.trans(
                                    'pages.product_type_sawline_edit.condition_field_empty_error',
                                    {
                                        field: error.field,
                                        conditionId: conditionId,
                                    }
                                );
                                showErrorMessage(errorMsg, () => {
                                    document
                                        .querySelector('#conditions-title')
                                        .scrollIntoView();
                                });
                                this.conditions[error.index].backendErrors = [
                                    errorMsg,
                                ];
                                break;
                            case 'sawcondition.invalid':
                                errorMsg = this.trans(
                                    'pages.product_type_sawline_edit.condition_field_invalid_error',
                                    {
                                        field: error.field,
                                        conditionId: conditionId,
                                    }
                                );
                                showErrorMessage(errorMsg, () => {
                                    document
                                        .querySelector('#conditions-title')
                                        .scrollIntoView();
                                });
                                this.conditions[error.index].backendErrors = [
                                    errorMsg,
                                ];
                                break;
                            case 'sawcondition.duplicate':
                                errorMsg = this.trans(
                                    'pages.product_type_sawline_edit.condition_not_unique_error',
                                    {
                                        conditionId: conditionId,
                                    }
                                );
                                showErrorMessage(errorMsg, () => {
                                    document
                                        .querySelector('#conditions-title')
                                        .scrollIntoView();
                                });
                                this.conditions[error.index].backendErrors = [
                                    errorMsg,
                                ];
                                break;
                            default:
                                showErrorMessage(
                                    this.trans(
                                        'pages.product_type_sawline_edit.unknown_save_error'
                                    )
                                );
                                break;
                        }
                        this.saving = false;
                    });
            }
        },
        getSawlinePayload(sawline) {
            const payload = {
                ID: sawline.ID,
                divideBy: sawline.divideBy,
                addition: sawline.addition,
                constant: sawline.constant,
                datafield: sawline.datafield,
                additionCNC: sawline.additionCNC,
                constantCNC: sawline.constantCNC,
                datafieldCNC: sawline.datafieldCNC,
                externalName: sawline.externalName,
                unit: sawline.unit,
                addBoorCorrection: sawline.addBoorCorrection,
                profile: sawline.profile,
                profileDatafield: sawline.profileDatafield,
                cnc: sawline.cnc,
                showPaintlist: sawline.showPaintlist,
                showSawlist: sawline.showSawlist,
                show_fly_screen: sawline.show_fly_screen,
                fly_screen_color: sawline.fly_screen_color,
                fly_screen_order: sawline.fly_screen_order,
                use_product_image_on_paint_list:
                    sawline.use_product_image_on_paint_list,
                leftEnd: sawline.leftEnd,
                rightEnd: sawline.rightEnd,
                add_sawcut_margin: sawline.add_sawcut_margin,
                removed: sawline.removed,
                parent_id: sawline.parent_id,
                offset: sawline.offset,
                conditions: [],
                productlistDefinitionIds: sawline.productlistDefinitionIds,
                flatIndex: this.flatSawlines.findIndex(
                    (x) => x.ID == sawline.ID
                ),
                children: (this.sawlines[sawline.ID] || []).map((x) =>
                    this.getSawlinePayload(x)
                ),
            };

            sawline.conditions.forEach((condition) => {
                if (!condition.removed) {
                    payload.conditions.push({
                        datafield: condition.datafield,
                        compare: condition.compare,
                        cmpValue: condition.cmpValue,
                    });
                }
            });

            return payload;
        },

        getErrorFromValidator(validator, skipDirtyCheck) {
            if (!validator || !validator.$invalid) {
                return false;
            }

            if (!validator.$dirty && !skipDirtyCheck) {
                return false;
            }

            if (validator.required === false) {
                return this.trans(
                    'pages.product_type_sawline_edit.validation_errors.required'
                );
            }
            if (validator.maxLength === false) {
                return this.trans(
                    'pages.product_type_sawline_edit.validation_errors.max_length',
                    {
                        max: validator.$params.maxLength.max,
                    }
                );
            }
            if (validator.isUnique === false) {
                return this.trans(
                    'pages.product_type_sawline_edit.validation_errors.unique'
                );
            }

            return false;
        },
        back() {
            this.$router.push('/settingsProducttypes');
        },
        addCondition() {
            const condition = {
                ID: null,
                cmpValue: '',
                compare: this.compareOperatorOptions[0].value,
                datafield: this.dataFieldOptions[0].value,
                removed: false,
                backendErrors: [],
            };
            this.conditions = [...this.conditions, condition];
        },
        addChildSawline(parentId) {
            const sawline = this.createSawline();
            this.openLine(sawline);
            sawline.parent_id = parentId;
            const allLines = this.sawlines;
            const arr = allLines[sawline.parent_id || ''] || [];
            arr.push(sawline);
            allLines[sawline.parent_id || ''] = arr;
            this.sawlines = { ...allLines };
        },
        addSawline() {
            const sawline = this.createSawline();
            this.openLine(sawline);
            const allLines = this.sawlines;
            const arr = allLines[sawline.parent_id || ''] || [];
            arr.push(sawline);
            allLines[sawline.parent_id || ''] = arr;
            this.sawlines = { ...allLines };
            this.$nextTick(() => {
                const element = this.$el.querySelector(
                    '[sawline-id="' + sawline.ID + '"]'
                );
                if (element) {
                    element.scrollIntoView();
                }
            });
        },
        copySawline(sawline) {
            const newSawline = JSON.parse(JSON.stringify(sawline));
            newSawline.ID = 'new.' + (this.flatSawlines.length + 1);
            newSawline.selectedConditionToAdd = null;
            newSawline.removed = false;
            const allLines = this.sawlines;
            const arr = allLines[newSawline.parent_id || ''] || [];
            arr.push(newSawline);
            allLines[newSawline.parent_id || ''] = arr;
            this.sawlines = { ...allLines };
            this.openLine(newSawline);
            this.$nextTick(() => {
                const element = this.$el.querySelector(
                    '[sawline-id="' + newSawline.ID + '"]'
                );
                if (element) {
                    element.scrollIntoView();
                }
            });
        },
        createSawline() {
            return {
                ID: 'new.' + (this.flatSawlines.length + 1),
                addition: '0',
                constant: '0',
                datafield: Object.keys(this.dataFields)[0] || null,
                additionCNC: '0',
                constantCNC: null,
                datafieldCNC: null,
                externalName: '',
                unit: '',
                addBoorCorrection: '0',
                profile: null,
                cnc: null,
                showPaintlist: '1',
                showSawlist: '1',
                show_fly_screen: '0',
                fly_screen_color: '01',
                fly_screen_order: '0',
                use_product_image_on_paint_list: '0',
                leftEnd: '0',
                rightEnd: '0',
                add_sawcut_margin: '1',
                sawConditions: [],
                conditions: [],
                selectedConditionToAdd: null,
                removed: false,
                backendErrors: [],
                parent_id: null,
                offset: 200,
                productlistDefinitionIds: [],
            };
        },
    },
    computed: {
        flatSawlines() {
            if (!this.sawlines) {
                return [];
            }
            return _.flatten(Object.values(this.sawlines));
        },
        currentLanguage() {
            return 'nl-BE';
        },
        compareOperatorOptions() {
            return this.compareOperators.map((x) => {
                return { value: x, label: x };
            });
        },
        dataFieldOptions() {
            const options = [];
            for (const [dataFieldId, dataField] of Object.entries(
                this.dataFields
            )) {
                const currentLang = this.currentLanguage;
                let label = this.getDatafieldLabel(dataField, currentLang);
                options.push({
                    value: dataField.datafieldId,
                    label: label,
                    active: dataField.inUse != 0,
                });
            }
            return options;
        },
    },
    watch: {},
    validations() {
        const component = this;
        const required = requiredIf(function (item) {
            return !item.removed;
        });
        return {
            conditions: {
                $each: {
                    isUnique(condition) {
                        if (condition.removed) {
                            return true;
                        }
                        const data = getJson(condition);
                        const allItems = component.conditions
                            .filter((x) => !x.removed)
                            .map((x) => getJson(x));
                        let count = allItems.reduce(
                            (n, x) => n + (x === data),
                            0
                        );
                        return count == 1;

                        function getJson(item) {
                            return JSON.stringify({
                                cmpValue: item.cmpValue,
                                compare: item.compare,
                                datafield: item.datafield,
                            });
                        }
                    },
                    cmpValue: {
                        required: required,
                        maxLength: maxLength(50),
                    },
                    compare: {
                        required: required,
                    },
                    datafield: {
                        required: required,
                    },
                    backendErrors: {},
                    removed: {},
                },
            },
        };
    },
    components: {
        SpinnerText,
    },
};
</script>
